import { useGroupRoutines } from '@gripp/shared-logic';
import {
  AddButton,
  AlertFormRef,
  Button,
  Colors,
  ColumnData,
  EditAlertForm,
  GroupAlertForm,
  Icon,
  LoadingIndicator,
  PanelShell,
  SlideAction,
  tokens
} from '@gripp/shared-ui';
import { MutableRefObject, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Pressable, StyleSheet, View } from 'react-native';
import { useNavigate, useParams } from 'react-router-dom';
import { GroupAlertListContent } from './group-alerts';
import { GroupsSelectionPanelMain } from './groupsSelectionPanel';

// ── Form header (reset button) ─────────────────────────────────────────────────

type AlertFormHeaderProps = {
  formRef: MutableRefObject<AlertFormRef>;
};

const AlertFormHeader = ({ formRef }: AlertFormHeaderProps) => (
  <Pressable
    onPress={() => formRef.current?.reset()}
    style={formStyles.resetButton}
    accessibilityRole="button"
  >
    <Icon name="Reset" size={20} color={Colors.black} />
  </Pressable>
);

// ── Page ───────────────────────────────────────────────────────────────────────

export const GroupAlertFormPage = () => {
  const { groupId, alertId } = useParams<{
    groupId: string;
    alertId?: string;
  }>();

  const navigate = useNavigate();
  const { routinesCount, isLoading: isRoutinesLoading } = useGroupRoutines(groupId);
  const { t } = useTranslation();
  const formRef = useRef<AlertFormRef>(null);
  const backGuardRef = useRef<
    ((onProceed: () => void, onCancel?: () => void) => void) | null
  >(null);

  if (!groupId) return null;

  const isEdit = !!alertId;

  const backButtonGuard = (): Promise<boolean> =>
    new Promise<boolean>((resolve) => {
      if (!backGuardRef.current) {
        resolve(true);
        return;
      }
      backGuardRef.current(
        () => resolve(true),
        () => resolve(false)
      );
    });

  const confirmBottomComponent = (onSubmit: () => void, disabled: boolean) => (
    <View>
      <Button
        variant="rectangular"
        size="large"
        state={disabled ? 'default' : 'active'}
        disabled={disabled}
        onPress={() => {
          if (!disabled) onSubmit();
        }}
        style={formStyles.confirmButton}
      >
        {t('alerts.confirm')}
      </Button>
    </View>
  );

  const leftColumnMain = isRoutinesLoading ? (
    <View style={formStyles.loadingContainer}>
      <LoadingIndicator
        backgroundColor={tokens.colors.neutral[100]}
        flexContainer
      />
    </View>
  ) : routinesCount > 0 ? (
    <GroupAlertListContent
      groupId={groupId}
      selectedAlertId={alertId}
      isCreatingNew={!isEdit}
    />
  ) : (
    <GroupsSelectionPanelMain
      groupIds={[groupId]}
      onComplete={() => navigate('../../..', { relative: 'path' })}
    />
  );

  const leftColumn: ColumnData = {
    title: 'alerts.alerts',
    mainComponent: leftColumnMain,
    headerComponent: (
      <AddButton
        variant="header"
        onPress={() => navigate(`../new`, { relative: 'path' })}
        accessibilityLabel="Create Alert"
      />
    ),
    showBackButton: true,
    backButtonTransition: SlideAction.PREV,
    onBackButtonPress: () => {
      navigate('../../..', { relative: 'path' });
    },
    disableScroll: true,
  };

  const rightColumn: ColumnData = {
    title: isEdit ? 'alerts.editAlert' : 'alerts.newAlert',
    mainComponent: isEdit && alertId ? (
      <View style={formStyles.container}>
        <EditAlertForm
          alertId={alertId}
          groupId={groupId}
          onClose={() => navigate('..', { relative: 'path' })}
          formRef={formRef}
          renderBottomComponent={confirmBottomComponent}
          onRegisterBackGuard={(guard) => {
            backGuardRef.current = guard;
          }}
        />
      </View>
    ) : (
      <View style={formStyles.container}>
        <GroupAlertForm
          groupId={groupId}
          onClose={() => navigate('..', { relative: 'path' })}
          formRef={formRef}
          renderBottomComponent={confirmBottomComponent}
        />
      </View>
    ),
    headerComponent: isEdit ? <AlertFormHeader formRef={formRef} /> : undefined,
    showBackButton: true,
    backButtonGuard: isEdit ? backButtonGuard : undefined,
    backButtonTransition: SlideAction.PREV,
    onBackButtonPress: () => navigate('..', { relative: 'path' }),
    disableScroll: true,
  };

  return (
    <PanelShell
      leftColumn={leftColumn}
      rightColumnOverride={rightColumn}
    />
  );
};

// ── Styles ─────────────────────────────────────────────────────────────────────

const formStyles = StyleSheet.create({
  container: {
    flex: 1,
  },
  loadingContainer: {
    flex: 1,
    backgroundColor: tokens.colors.neutral[100],
  },
  resetButton: {
    padding: tokens.spacing.xs,
  },
  confirmButton: {
    width: '100%',
  },
});
